export enum MarketType {
  LOW_HIGH = 'LOW_HIGH',
  WHO_WIN = 'WHO_WIN',
  ODDS_EVEN = 'ODDS_EVEN',
  BLACK_OR_RED = 'BLACK_OR_RED',
  PAIR = 'PAIR',
  RANK = 'RANK',
  ANDAR_BAHAR_SIDE = 'ANDAR_BAHAR_SIDE',
  ANDAR_BAHAR_FIRST_BET = 'ANDAR_BAHAR_FIRST_BET',
  ANDAR_BAHAR_SECOND_BET = 'ANDAR_BAHAR_SECOND_BET',
  ODDS_EVEN_TEEN_PATTI_ONE_DAY = 'ODDS_EVEN_TEEN_PATTI_ONE_DAY',
  WINNERS = 'WINNERS',
  SUIT = 'SUIT',
  WHO_WIN_TEEN_PATTI_ONE_DAY = 'WHO_WIN_TEEN_PATTI_ONE_DAY',
  CONSECUTIVE = 'CONSECUTIVE',
  RANK_LIST = 'RANK_LIST',
  LUCKY_5_LINE_BET = 'LUCKY_5_LINE_BET',
  LOW_HIGH_5 = 'LOW_HIGH_5',
  CORRECT_RANK_LUCKY_5 = 'CORRECT_RANK_LUCKY_5',
  JHANDI_MUNDA_WINNERS = 'JHANDI_MUNDA_WINNERS',
}

export enum MarketStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  LOCKED = 'LOCKED',
}

export enum BettingType {
  MAIN = 'MAIN',
  SIDE = 'SIDE',
}

export interface Options {
  id: string;
  label: string;
  reference: string;
  selected: boolean;
}

// Betting window on a market. The wire sends ISO strings here.
export interface BettingTime {
  start: string;
  end: string;
  serverTime?: string;
}

export interface Market {
  id: string;
  configId: string;
  roundId: string;
  gameId: string;
  name: string;
  type: MarketType;
  reference: string;
  marketOpenTime: Date;
  marketCloseTime: Date;
  status: MarketStatus;
  sortOrder: number;
  selections: Selection[];
  marketGroupName: string;
  layEnabled: boolean;
  bettingType: BettingType;
  bettingTimes?: BettingTime[];
}

// A selection plus its market details, used to build a bet payload.
export type SelectionWithMarket = Selection & {
  marketId: string;
  roundId: string;
  gameId: string;
  marketReference: string;
  marketStatus: MarketStatus;
  market: Market;
};

export interface Selection {
  id: string;
  reference: string;
  name: string;
  status: SelectionStatus;
  backPrices: Price[];
  layPrices: Price[];
  groupInfo: {
    name: string;
    sortOrder: number;
  };
  layEnabled: boolean;
  options: Options[];
}

export enum SelectionStatus {
  ACTIVE = 'ACTIVE',
  WINNER = 'WINNER',
  LOSER = 'LOSER',
  VOIDED = 'VOIDED',
  LOCKED = 'LOCKED',
}

export interface Price {
  id: string;
  side: PriceSide;
  value: number;
}

export enum PriceSide {
  BACK = 'BACK',
  LAY = 'LAY',
}

export enum OptionType {
  USER_OPTIONS = 'USER_OPTIONS',
  BACK_LAY = 'BACK_LAY',
}

export interface PlaceBetPayload {
  clientBetId: string;
  amount: number;
  selectionId: string;
  priceId: string;
  marketId: string;
  roundId: string;
  gameId: string;
  userPlacedTime: string;
  clientId: string;
  side: PriceSide;
  type?: OptionType;
  option?: Record<string, unknown>;
}

export type BetStatus =
  | 'PLACED'
  | 'ACCEPTED'
  | 'CANCELLED'
  | 'SETTLED'
  | 'REJECTED'
  | 'VOIDED';

export type betErrorObject = {
  errorCode: string | null;
  field: string;
  message: string;
};

export type BetOutcome = 'WIN' | 'LOST' | 'VOID';

export interface Bet {
  id: string;
  systemId: number;
  clientBetId: string;
  userId: number;
  amount: number;
  amountF: number;
  selectionId: string;
  priceId: string;
  marketId: string;
  roundId: string;
  gameId: string;
  price: number;
  userPlacedTime: number;
  status: BetStatus;
  invalid: boolean;
  externalId: string;
  side: 'BACK' | 'LAY';
  errors: betErrorObject[];
  clientId: string;
  connectSessionId: string;
  operatorId: string;
  createdAt: Date | null;
  lastUpdatedAt: Date | null;
  version: number;
  position: string;
  /**
   * Settlement, present once `status` is `SETTLED`: what this bet won
   * in the player's currency, and the multiplier the server applied.
   * The client never computes a payout — it reads these.
   */
  userCurrencyWinAmount?: number;
  settleOdds?: number;
  outcome: BetOutcome;
  // Cash-out fields, sent for games that support cash-out (bdlj, chicken).
  cashoutStatus?: CashoutStatus | null;
  userCashoutTime?: string | null;
  cashoutTime?: string | null;
  autoCashoutOn?: number | null;
}

export type CashoutStatus = 'CASHOUT_REQUESTED' | 'CASHOUT';

export interface BetState {
  bets: Bet[];
}
